import { Alert, Form, GetProp, Input, InputNumber, Modal, Row, Select, Tabs, Upload, UploadFile, UploadProps } from "antd"
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { serviceLoading } from "src/common/apiLoader/state/selection";
import { SystemDescriptions } from "src/common/descriptions";
import { ServiceCategory } from "src/common/models/serviceCategory";
import { User } from "src/common/models/user";
import { getInputRequiredRule } from "src/common/util";
import { RichTextEditor } from "src/common/components/RichTextEditor";
import { Col10, Col12, Col24, Col4, Col5 } from "src/components/Columns";
import { FormActionButtons } from "src/components/FormActionButtons";
import { UPSERT_SERVICE_CATEGORY_API } from "src/features/Service/state/actionTypes";
import { ServiceError } from "src/features/Service/state/types";
import { RootState } from "src/state/reducer";
import { PlusOutlined } from "@ant-design/icons";

interface ReduxProps {
    error?: ServiceError
    user?: User
    isUpsertingServiceCategory: boolean
}

interface Props {
    initialValues?: ServiceCategory
    onFinish: (serviceCategory: Partial<ServiceCategory>) => void
    onCancel: () => void
}

export const ServiceCategoryForm = (props: Props) => {
    const [form] = Form.useForm();
    const [fileList, setFileList] = useState<UploadFile[]>([])
    const [previewOpen, setPreviewOpen] = useState(false)
    const [previewImage, setPreviewImage] = useState("")
    const [hasIconChanged, setHasIconChanged] = useState(false)

    const reduxProps: ReduxProps = useSelector((state: RootState) => ({
        error: state.service.error,
        user: state.security.user,
        isUpsertingServiceCategory: serviceLoading(state, [UPSERT_SERVICE_CATEGORY_API]),
    }))

    const descriptions = SystemDescriptions.PAGES.SERVICE.CREATE_SERVICE_CATEGORY
    const commonDescriptions = SystemDescriptions.PAGES.COMMON

    useEffect(() => {
        form.setFieldsValue({
            name: props.initialValues?.name,
            status: props.initialValues?.isActive === false ? 2 : 1,
            proposalTitle: props.initialValues?.proposalTitle,
            proposalDescription: props.initialValues?.proposalDescription,
            proposalIconUrl: props.initialValues?.proposalIconUrl,
            proposalBenefits: props.initialValues?.proposalBenefits?.join("\n"),
            proposalPromotionText: props.initialValues?.proposalPromotionText,
            proposalFooterNote: props.initialValues?.proposalFooterNote,
            proposalDisplayOrder: props.initialValues?.proposalDisplayOrder,
            proposalIsEnabled: props.initialValues?.proposalIsEnabled === false ? 2 : 1,
        })

        if (props.initialValues?.proposalIconUrl) {
            setPreviewImage(props.initialValues.proposalIconUrl)
            setFileList([
                {
                    uid: props.initialValues.id?.toString() || props.initialValues.proposalIconUrl,
                    name: `${props.initialValues.name}.jpg`,
                    status: "done",
                    url: props.initialValues.proposalIconUrl,
                },
            ])
        } else {
            setPreviewImage("")
            setFileList([])
        }

        setHasIconChanged(false)
    }, [form, props.initialValues])

    const getProposalBenefits = (benefits?: string): string[] => (
        benefits
            ?.split("\n")
            .map(benefit => benefit.trim())
            .filter(Boolean) || []
    )

    type FileType = Parameters<GetProp<UploadProps, 'beforeUpload'>>[0];

    const getBase64 = (file: FileType): Promise<string> => (
        new Promise((resolve, reject) => {
            const reader = new FileReader()
            reader.readAsDataURL(file as any)
            reader.onload = () => resolve(reader.result as string)
            reader.onerror = (error) => reject(error)
        })
    )

    const getProposalIconBase64 = async (): Promise<string | undefined> => {
        if (!hasIconChanged) return undefined

        return fileList.length > 0
            ? await getBase64(fileList[0].originFileObj as any)
            : ""
    }

    const onFinish = async (formValues) => {
        const serviceCategory: ServiceCategory = {
            name: formValues.name,
            isActive: formValues.status === 1,
            proposalTitle: formValues.proposalTitle,
            proposalDescription: formValues.proposalDescription,
            proposalIconUrl: hasIconChanged ? "" : props.initialValues?.proposalIconUrl,
            proposalIconBase64: await getProposalIconBase64(),
            previousProposalIconUrl: hasIconChanged ? props.initialValues?.proposalIconUrl : undefined,
            proposalBenefits: getProposalBenefits(formValues.proposalBenefits),
            proposalPromotionText: formValues.proposalPromotionText,
            proposalFooterNote: formValues.proposalFooterNote,
            proposalDisplayOrder: formValues.proposalDisplayOrder,
            proposalIsEnabled: formValues.proposalIsEnabled === 1,
            createdAt: '',
            updatedAt: '',
            userId: reduxProps.user?.id!,
        }

        if (props.initialValues && props.initialValues.id) {
            serviceCategory.id = props.initialValues.id
        }

        clearForm()
        props.onFinish(serviceCategory)
    }

    const getButtonStatus = (): boolean => {
        return props.initialValues
            ? false
            : !form.isFieldsTouched(true) ||
            form.getFieldsError().filter(({ errors }) => errors.length)
                .length > 0
    }

    const clearForm = () => {
        form.resetFields()
        setFileList([])
        setPreviewImage("")
        setHasIconChanged(false)
    }

    const handleCancelClick = () => {
        clearForm()
        props.onCancel()
    }

    const renderButtons = () => (
        <FormActionButtons
            cancelText={descriptions.CANCEL_BUTTON}
            actionText={descriptions.SAVE_BUTTON}
            actionDisabled={getButtonStatus}
            onCancel={handleCancelClick}
            actionLoading={reduxProps.isUpsertingServiceCategory}
        />
    )

    const renderGeneralTab = () => (
        <Row gutter={[16, 8]}>
            <Col12>
                <Form.Item
                    label={descriptions.FORM.NAME.LABEL}
                    name={"name"}
                    rules={[
                        getInputRequiredRule("name"),
                    ]}
                    validateTrigger="onBlur"
                >
                    <Input
                        placeholder={descriptions.FORM.NAME.PLACEHOLDER}
                    />
                </Form.Item>
            </Col12>
            <Col12>
                <Form.Item
                    label={descriptions.FORM.IS_ACTIVE.LABEL}
                    name={"status"}
                    validateTrigger="onBlur"
                    rules={[
                        getInputRequiredRule("status"),
                    ]}
                >
                    <Select
                        options={[
                            {
                                value: 1,
                                label: commonDescriptions.ACTIVE_STATUS
                            },
                            {
                                value: 2,
                                label: commonDescriptions.INACTIVE_STATUS,
                            },
                        ]}
                        placeholder={descriptions.FORM.IS_ACTIVE.PLACEHOLDER}
                    />
                </Form.Item>
            </Col12>
        </Row>
    )

    const handlePreview = async (file) => {
        if (!file.url && !file.preview) {
            file.preview = await getBase64(file.originFileObj)
        }
        setPreviewImage(file.url || file.preview)
        setPreviewOpen(true)
    }

    const handleIconChange = ({ fileList: newFileList }) => {
        setFileList(newFileList)
        setHasIconChanged(true)
    }

    const beforeUpload = (file) => {
        const allowedImageTypes: string[] = [
            "image/jpeg",
            "image/jpg",
            "image/png",
            "image/webp",
            "image/svg+xml",
        ]

        const isAllowed = allowedImageTypes.includes(file.type)
        if (!isAllowed) {
            alert("El formato de la imagen no es permitido")
        }

        return isAllowed || Upload.LIST_IGNORE
    }

    const renderProposalIconSelector = () => (
        <Form.Item label="Icono de propuesta">
            <Upload
                listType="picture-card"
                fileList={fileList}
                onPreview={handlePreview}
                onChange={handleIconChange}
                beforeUpload={beforeUpload}
                showUploadList={{ showRemoveIcon: true }}
                maxCount={1}
                customRequest={({ onSuccess }) => {
                    setTimeout(() => {
                        if (onSuccess) onSuccess("ok")
                    }, 0)
                }}
            >
                {
                    fileList.length >= 1
                        ? null
                        : <div>
                            <PlusOutlined />
                            <div style={{ marginTop: 8 }}>Subir</div>
                        </div>
                }
            </Upload>
            <Modal open={previewOpen} footer={null} onCancel={() => setPreviewOpen(false)}>
                <img alt="preview" style={{ width: "100%" }} src={previewImage} />
            </Modal>
        </Form.Item>
    )

    const renderProposalTab = () => (
        <Row gutter={[16, 8]}>
            <Col10>
                <Form.Item
                    label="Titulo en propuesta"
                    name={"proposalTitle"}
                    validateTrigger="onBlur"
                >
                    <Input placeholder="Ej. Pantalla LED" />
                </Form.Item>
            </Col10>
            <Col5>
                <Form.Item
                    label="Mostrar en portada"
                    name={"proposalIsEnabled"}
                    validateTrigger="onBlur"
                >
                    <Select
                        options={[
                            {
                                value: 1,
                                label: commonDescriptions.ACTIVE_STATUS
                            },
                            {
                                value: 2,
                                label: commonDescriptions.INACTIVE_STATUS,
                            },
                        ]}
                        placeholder="Selecciona estado"
                    />
                </Form.Item>
            </Col5>
            <Col5>
                <Form.Item
                    label="Orden en propuesta"
                    name={"proposalDisplayOrder"}
                    validateTrigger="onBlur"
                >
                    <InputNumber
                        style={{ width: "100%" }}
                        min={1}
                        placeholder="Ej. 1"
                    />
                </Form.Item>
            </Col5>
            <Col4>
                {renderProposalIconSelector()}
            </Col4>
            <Col24>
                <Form.Item
                    label="Detalle corto para portada"
                    name={"proposalDescription"}
                    validateTrigger="onBlur"
                >
                    <RichTextEditor
                        minHeight={150}
                        placeholder="Ej. Calzada Revolucion del 7, San Marcos, por estatua JRB."
                    />
                </Form.Item>
            </Col24>
            <Col24>
                <Form.Item
                    label="Beneficios"
                    name={"proposalBenefits"}
                    validateTrigger="onBlur"
                >
                    <Input.TextArea
                        rows={5}
                        placeholder={"Un beneficio por linea"}
                    />
                </Form.Item>
            </Col24>
            <Col24>
                <Form.Item
                    label="Promocion"
                    name={"proposalPromotionText"}
                    validateTrigger="onBlur"
                >
                    <RichTextEditor
                        minHeight={90}
                        placeholder="Ej. Edicion de video gratis al contratar 3 meses o mas"
                    />
                </Form.Item>
            </Col24>
            <Col24>
                <Form.Item
                    label="Nota inferior de precios"
                    name={"proposalFooterNote"}
                    validateTrigger="onBlur"
                >
                    <RichTextEditor
                        minHeight={90}
                        placeholder="Ej. No incluye edicion"
                    />
                </Form.Item>
            </Col24>
        </Row>
    )

    const renderForm = () => (
        <Form
            form={form}
            layout="vertical"
            requiredMark={false}
            autoComplete={'none'}
            onFinish={onFinish}
            initialValues={{
                status: 1,
                proposalIsEnabled: 1,
            }}
        >
            <Tabs
                defaultActiveKey="general"
                items={[
                    {
                        key: "general",
                        label: "General",
                        children: renderGeneralTab(),
                    },
                    {
                        key: "proposal",
                        label: "Propuesta",
                        children: renderProposalTab(),
                    },
                ]}
            />
            {
                reduxProps.error &&
                <Alert
                    message={"ERROR"}
                    showIcon
                    description={reduxProps.error?.detail.message ?? commonDescriptions.FORM.UNKNOWN_ERROR}
                    type="error"
                    style={{
                        marginBottom: 12,
                    }}
                />
            }
            {renderButtons()}
        </Form>
    )

    return (
        renderForm()
    )
}
